import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { Calendar } from 'lucide-react'
import dayjs from 'dayjs'

import { extractApiError, FormModal } from '@components/branded-modal'
import { getFriendlyErrorMessage } from '../../../utils/errorMessage'
import { AdasBox } from '@components/wrapper-components'
import { AdsCustomModal } from '@components/wrapper-components'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useMessageStore } from '@caradasstore/MessageStore'
import {
  createAppointment,
  createAppointmentCalibration,
  createAppointmentEstimate,
  getDocuments,
  getFile,
  updateQuoteSubStatus
} from 'api/api'
import { SubStatusEnum } from 'enums/enums'
import { validations } from '../../../utils/utils'
import { Logger } from 'logger'
import { appointmentInitialDetails } from './helper'
import { useLocation } from 'react-router-dom'
import { AppointmentForm } from './AppointmentForm'
import { AppointmentPayload } from './AppointmentPayload'
import { useUserStore } from '@caradasstore/UserStore'
import { useSchedulerOrganisation, SCHEDULER_ORGANISATION_STATUS } from '@hooks/useSchedulerOrganisation'
import { MAX_FILE_SIZE_MB, DATE_FORMATS, BUTTONS_DISPLAY_LABEL } from 'constants'
import { DataOverrideCard } from './DataOverrideCard'
import { findUpcomingAppointmentsForVin } from '../existing-appointment/existingAppointment'
import { UpcomingAppointmentBanner } from '../existing-appointment/UpcomingAppointmentBanner'
import { QuoteAppointmentDetails } from '../existing-appointment/ExistingAppointmentDialog'
import { MIME_TYPES_MAP } from 'variable-maps'

// Shown instead of the form until the center's scheduler organisation is known.
const ORGANISATION_STATUS_MESSAGES = {
  [SCHEDULER_ORGANISATION_STATUS.LOADING]: 'Loading scheduler details...',
  [SCHEDULER_ORGANISATION_STATUS.MISSING]:
    'The scheduler is not set up for this center yet. Set it up from the Calendar page to book appointments.',
  [SCHEDULER_ORGANISATION_STATUS.ERROR]: 'Could not load the scheduler details. Close this form and try again.'
}

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png'
]
export const CreateAppointmentForm = ({ open = true, onClose, setRefresh, prefill, linkedQuoteId, linkedQwiId, onSaved }) => {
  const [cancelAppointmentModal, setCancelAppointmentModal] = useState(false)
  const [apiError, setApiError] = useState(null)
  const currentUser = useUserStore((store) => store.currentUser)
  const addMessage = useMessageStore((store) => store.addMessage)
  const [availabilities, setAvailabilities] = useState({
    overrides_date: '', // You can provide a default date or empty string
    slots: [] // Default empty array for slots
  })

  const [formValues, setFormValues] = useState({
    ...appointmentInitialDetails,
    appointmentDate: dayjs().format(DATE_FORMATS.ISO_DATE_STRING),
    useVin: appointmentInitialDetails.useVin || false,
    pickupContact: {
      fullName: '',
      address: '',
      city: '',
      phone: '',
      state: '',
      zipCode: ''
    },
    // CAR-3678 — prefill from the originating quote (contact / vehicle / RO) and
    // carry its qwi_mapping id as the link so the saved appointment ties back to it.
    // The scheduler stores a qwi_mapping id, not a quote id: the two are different
    // sequences, so passing the quote id here linked the appointment to the wrong
    // record (and a client user's booking was refused as not its own).
    ...(prefill || {}),
    qwi_id: linkedQwiId || appointmentInitialDetails.qwi_id
  })
  const [formErrors, setFormErrors] = useState({
    recentEstimate: '',
    calibrationFile: ''
  })
  // CAR-3875 — the quote's Calibration Required Report; the form has no field for it, it is
  // only carried through to the appointment's "Calibration ID Report" on save.
  const [quoteCalibrationReport, setQuoteCalibrationReport] = useState(null)
  // Booking from a quote only asks for date, time and pickup; the rest is the quote's. Save waits
  // for the quote's documents so a fast click never books without them.
  const isFromQuote = Boolean(linkedQuoteId)
  const [quoteDocumentsLoading, setQuoteDocumentsLoading] = useState(isFromQuote)
  const [formValid, setFormValid] = useState({
    fullName: true,
    primaryEmail: true,
    secondaryEmail: true,
    businessPhone: true,
    phone: true,
    referral: true,
    address: true,
    city: true,
    zipCode: true,
    state: true,
    aro: true,
    pickupContact: {
      fullName: true,
      address: true,
      city: true,
      phone: true,
      zipCode: true
    }
  })

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true) // New state for submit button
  const location = useLocation()
  const prePopulateVin = location.state?.prepopulateVin
  // Booking from a quote opens this form without the Calendar page having loaded the
  // organisation, so load it here; the form waits until it belongs to this center.
  const { status: organisationStatus, organisation: organisationDetail } = useSchedulerOrganisation()
  const isOrganisationReady = organisationStatus === SCHEDULER_ORGANISATION_STATUS.READY
  const ccId = useCenterStore((store) => store.ccId)

  // CAR-4034 — on the Scheduler (no quote), warn once the VIN already has an upcoming appointment. Booking from a
  // quote has already been through the quote's own check, so it is not asked twice.
  const [upcomingForVin, setUpcomingForVin] = useState([])
  const [viewingAppointmentId, setViewingAppointmentId] = useState(null)
  const organisationId = organisationDetail?.id
  useEffect(() => {
    setUpcomingForVin([])
    if (isFromQuote || !formValues.useVin || !formValues.vin) return undefined
    let cancelled = false
    findUpcomingAppointmentsForVin({ ccId, organisationId, vin: formValues.vin }).then((found) => {
      if (!cancelled) setUpcomingForVin(found)
    })
    return () => {
      cancelled = true
    }
  }, [ccId, organisationId, isFromQuote, formValues.useVin, formValues.vin])
  const setLoading = useLoadingStore((store) => store.setLoading)
  const history = useHistory()

  const validationMap = {
    fullName: validations.requiredAlphanumericText,
    primaryEmail: validations.email,
    secondaryEmail: validations.optionalEmail,
    businessPhone: validations.optionalPhone,
    phone: validations.optionalPhone,
    referral: validations.alphanumericText,
    address: validations.alphanumericText,
    city: validations.alphabeticText,
    zipCode: validations.optionalZip,
    aro: validations.alphanumericText,
    pickupContact: {
      fullName: validations.alphanumericText,
      address: validations.alphanumericText,
      city: validations.alphabeticText,
      phone: validations.optionalPhone,
      zipCode: validations.optionalZip
    }
  }

  useEffect(() => {
    if (currentUser) {
      setFormValues((prevState) => ({
        ...prevState,
        userId: currentUser.id,
        userName: currentUser.name
      }))
    }
  }, [currentUser])

  // CAR-3875 — booking from a quote carries its Collision Repair Estimate into "Most Recent
  // Estimate" and its Calibration Required Report into the appointment's "Calibration ID Report";
  // both are uploaded with the appointment on save. Best-effort: the form stays usable without
  // them, and an estimate the user picks first is never overwritten.
  useEffect(() => {
    if (!linkedQuoteId) return undefined
    let cancelled = false
    // Each download fails on its own, so a missing estimate never drops the calibration report.
    const toFile = async (attachment) => {
      try {
        const fileRes = await getFile({ attachmentId: attachment.id })
        if (!fileRes?.data) return null
        const name = attachment.name || 'document.pdf'
        const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : ''
        return new File([fileRes.data], name, { type: MIME_TYPES_MAP[ext] || fileRes.data.type })
      } catch (error) {
        Logger.error({ message: error, payload: { linkedQuoteId, attachmentId: attachment.id, path: 'CreateAppointmentForm/toFile' } })
        return null
      }
    }
    const prefillDocuments = async () => {
      try {
        const docsRes = await getDocuments({ type: 'quotes', id: linkedQuoteId })
        if (cancelled) return
        const { collisionRepairEstimate: estimate, calibrationRequiredReport: calibration } = docsRes?.data || {}
        const [estimateFile, calibrationFile] = await Promise.all([
          estimate?.id ? toFile(estimate) : null,
          calibration?.id ? toFile(calibration) : null
        ])
        if (cancelled) return
        if (estimateFile) setFormValues((prev) => (prev.recentEstimate ? prev : { ...prev, recentEstimate: estimateFile }))
        if (calibrationFile) setQuoteCalibrationReport(calibrationFile)
      } catch (error) {
        Logger.error({ message: error, payload: { linkedQuoteId, path: 'CreateAppointmentForm/prefillDocuments' } })
      } finally {
        if (!cancelled) setQuoteDocumentsLoading(false)
      }
    }
    prefillDocuments()
    return () => {
      cancelled = true
    }
  }, [linkedQuoteId])

  useEffect(() => {
    const areRequiredFieldsFilled = () => {
      // Client and vehicle come from the quote and aren't editable here, so only the slot is required.
      if (isFromQuote) return formValues.appointmentDate && formValues.appointmentTime
      const { fullName, primaryEmail, appointmentDate, appointmentTime, businessPhone, vehicleYear, vehicleMake, vehicleModel } = formValues
      return fullName && primaryEmail && appointmentDate && appointmentTime && vehicleYear && vehicleMake && vehicleModel && businessPhone
    }

    const areAllFieldsValid = () => {
      const { fullName, primaryEmail, secondaryEmail, phone, referral, address, city, zipCode, state, aro, pickupContact, businessPhone } =
        formValid

      const {
        fullName: pickupFullName,
        address: pickupAddress,
        city: pickupCity,
        phone: pickupPhone,
        zipCode: pickupZipCode
      } = pickupContact

      const { vin, useVin } = formValues // Destructure vin and useVin from formValues

      const isVinValid = !useVin || (useVin && vin)

      return (
        fullName &&
        primaryEmail &&
        secondaryEmail &&
        businessPhone &&
        phone &&
        referral &&
        address &&
        city &&
        zipCode &&
        state &&
        aro &&
        pickupFullName &&
        pickupAddress &&
        pickupCity &&
        pickupPhone &&
        pickupZipCode &&
        isVinValid
      )
    }

    const isFormValid = () => {
      if (isFromQuote) return Boolean(areRequiredFieldsFilled())
      return areRequiredFieldsFilled() && areAllFieldsValid()
    }
    setIsSubmitDisabled(!isFormValid())
  }, [formValues])

  const handleChange = (eventOrValue) => {
    if (eventOrValue && eventOrValue.target) {
      const { name, value, type, checked, files } = eventOrValue.target

      // The estimate, and when booking from a quote its calibration report, are both picked here.
      if ((name === 'recentEstimate' || name === 'calibrationFile') && files) {
        const selectedFile = files[0]
        if (!selectedFile) return
        if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
          setFormErrors((prev) => ({
            ...prev,
            [name]: 'Invalid file type. Please upload a PDF, DOCX, JPG, or PNG file.'
          }))
          addMessage({
            text: 'Invalid file type. Please upload a PDF, DOCX, JPG, or PNG file.'
          })

          return
        }
        if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          setFormErrors((prev) => ({
            ...prev,
            [name]: `File size exceeds the limit of ${MAX_FILE_SIZE_MB}MB.`
          }))
          addMessage({
            text: `File size exceeds the limit of ${MAX_FILE_SIZE_MB}MB.`
          })
          return
        }
        if (selectedFile.name?.length > 80) {
          setFormErrors((prev) => ({
            ...prev,
            [name]: `The file name cannot exceed more than 80 characters.`
          }))
          addMessage({
            text: `The file name cannot exceed more than 80 characters.`
          })
          return
        }

        if (name === 'calibrationFile') {
          setQuoteCalibrationReport(selectedFile)
        } else {
          setFormValues((prev) => ({
            ...prev,
            [name]: selectedFile
          }))
        }

        setFormErrors((prev) => ({
          ...prev,
          [name]: ''
        }))

        if (validationMap[name]) {
          setFormValid((prev) => ({
            ...prev,
            [name]: validationMap[name](value.trim())
          }))
        }
      } else {
        if (validationMap[name]) {
          setFormValid((prev) => ({
            ...prev,
            [name]: validationMap[name](value.trim())
          }))
        }
        setFormValues((prev) => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }))
      }
    } else if (eventOrValue) {
      setFormValues((prev) => ({
        ...prev,
        appointmentDate: eventOrValue
      }))
    }
  }

  const handleFileDelete = (field) => {
    if (field === 'calibrationFile') {
      setQuoteCalibrationReport(null)
      setFormErrors((prev) => ({ ...prev, calibrationFile: '' }))
      return
    }
    setFormValues((prev) => ({
      ...prev,
      recentEstimate: null
    }))

    setFormErrors((prev) => ({
      ...prev,
      recentEstimate: ''
    }))
  }

  // CAR-3634: one friendly, mapped message — never raw server text.
  const handleCustomError = (error) => {
    addMessage({ text: getFriendlyErrorMessage(error) })
  }

  const handleSubmit = async () => {
    if (!isOrganisationReady) return
    setLoading(true)
    setApiError(null)
    const appointmentPayload = AppointmentPayload({
      formValues,
      organisationDetail
    })
    try {
      const response = await createAppointment(ccId, appointmentPayload)
      if (response && response.status === 200) {
        submitAppointmentEstimate(response.data.id)
        submitAppointmentCalibration(response.data.id)
        // CAR-3678 — booking from a quote flips its sub-status to Scheduled
        // (main OPEN/CLOSED status is untouched — CAR-3491 rules). Best-effort:
        // a failure here shouldn't block the created appointment.
        if (linkedQuoteId) {
          try {
            await updateQuoteSubStatus({ quoteId: linkedQuoteId, subStatus: SubStatusEnum.SCHEDULED.value })
          } catch (subStatusError) {
            Logger.error({
              message: subStatusError,
              payload: { linkedQuoteId, path: 'CreateAppointmentForm/updateQuoteSubStatus' }
            })
          }
        }
        setRefresh?.((prev) => !prev)
        onSaved?.()
        onClose()
      }
    } catch (error) {
      Logger.error({
        message: error,
        payload: { appointmentPayload, path: 'handleSubmit' }
      })
      setApiError(extractApiError(error) || 'Failed to create appointment. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const submitAppointmentEstimate = async (appointmentId) => {
    if (formValues.recentEstimate instanceof File) {
      const formData = new FormData()
      formData.append('file', formValues.recentEstimate)

      const uploadResponse = await createAppointmentEstimate(ccId, appointmentId, formData)
    }
  }

  const submitAppointmentCalibration = async (appointmentId) => {
    if (!(quoteCalibrationReport instanceof File)) return
    try {
      const formData = new FormData()
      formData.append('file', quoteCalibrationReport)
      await createAppointmentCalibration(ccId, appointmentId, formData)
    } catch (error) {
      Logger.error({ message: error, payload: { appointmentId, path: 'CreateAppointmentForm/submitAppointmentCalibration' } })
    }
  }

  const handleCancel = () => {
    setCancelAppointmentModal(true)
  }

  const handleCloseModal = (event, reason) => {
    if (reason && reason === 'backdropClick') return
    setCancelAppointmentModal(false)
    onClose()
  }

  const footer = (
    <>
      {availabilities?.overrides_date && <DataOverrideCard availabilities={availabilities} />}
      <AdasBox className='flex justify-end gap-3'>
        <button
          type='button'
          onClick={handleCancel}
          className='inline-flex items-center justify-center rounded-lg border border-[#D1D5D8] bg-white px-5 py-2.5 text-sm font-medium text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
        >
          Cancel
        </button>
        <button
          type='button'
          onClick={handleSubmit}
          disabled={isSubmitDisabled || !isOrganisationReady || quoteDocumentsLoading}
          className='inline-flex items-center justify-center rounded-lg bg-sidebar px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sidebar/90 disabled:cursor-not-allowed disabled:opacity-50'
        >
          Save Appointment
        </button>
      </AdasBox>
    </>
  )

  return (
    <>
      <FormModal
        open={open}
        onClose={onClose}
        title={BUTTONS_DISPLAY_LABEL.NEW_APPOINTMENT}
        icon={Calendar}
        footer={footer}
        bodyClassName='pt-4 pb-4'
        mobileFullScreen
        errorMessage={apiError}
        onDismissError={() => setApiError(null)}
        // Booking is the one write a client user may make, so the form stays fillable.
        clientMayEdit
      >
        {isOrganisationReady && <UpcomingAppointmentBanner appointments={upcomingForVin} onView={setViewingAppointmentId} />}
        {!isOrganisationReady ? (
          <AdasBox className='py-10 text-center text-sm text-muted-foreground' role='status'>
            {ORGANISATION_STATUS_MESSAGES[organisationStatus]}
          </AdasBox>
        ) : (
          <AppointmentForm
            formValues={formValues}
            handleChange={handleChange}
            prePopulateVin={prePopulateVin}
            type={false}
            setFormValues={setFormValues}
            formErrors={formErrors}
            handleFileDelete={handleFileDelete}
            formValid={formValid}
            setFormValid={setFormValid}
            validationMap={validationMap}
            setAvailabilities={setAvailabilities}
            quoteLocked={isFromQuote}
            quoteCalibrationReport={quoteCalibrationReport}
            quoteDocumentsLoading={quoteDocumentsLoading}
          />
        )}
      </FormModal>

      {viewingAppointmentId && (
        <QuoteAppointmentDetails appointmentId={viewingAppointmentId} onClose={() => setViewingAppointmentId(null)} />
      )}

      <AdsCustomModal
        open={cancelAppointmentModal}
        onClose={handleCloseModal}
        title='Cancel Appointment'
        description='Are you sure you want to cancel this appointment?'
        successButtonTitle={'Confirm'}
        cancelButtonTitle={'No'}
        cancelButtonConfig={{
          variant: 'outlined'
        }}
      />
    </>
  )
}

CreateAppointmentForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  setRefresh: PropTypes.func,
  // CAR-3678 — internal "book from a quote" path: the quote id drives the sub-status
  // update and document prefill, its qwi_mapping id is what the appointment links to.
  prefill: PropTypes.object,
  linkedQuoteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  linkedQwiId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSaved: PropTypes.func
}
